import { db } from "./firebase";
import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  where,
  deleteDoc,
} from "firebase/firestore";
import {
  MODULES,
  arrayToMap,
  emptyPermissionMap,
  grantAllPermissions,
  mapToArray,
  normalizePermissionMap,
} from "../security/permissionRegistry";
import { usesLocalApi } from "../services/deploymentConfig";
import { localPermissionService } from "../services/permissionLocalApiService";

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const PREDEFINED_ROLE_REGISTRY_VERSION = "2026-06-local-server-permissions-v6";

const hasAnyTrue = (permMap) =>
  isPlainObject(permMap) &&
  Object.values(permMap).some(
    (mod) => isPlainObject(mod) && Object.values(mod).some((v) => v === true)
  );

const buildRoleMap = (rules = {}) => {
  const map = emptyPermissionMap();
  for (const [moduleKey, actions] of Object.entries(rules)) {
    if (!MODULES[moduleKey]) continue;
    for (const action of actions || []) {
      if (MODULES[moduleKey].actions.includes(action)) {
        map[moduleKey][action] = true;
      }
    }
  }
  return normalizePermissionMap(map);
};

const clinicalRead = ["view"];
const operationalEdit = ["view", "create", "edit"];
const approveFlow = ["view", "create", "edit", "approve", "export"];
const reporting = ["view", "export"];

const predefinedRoleDefinitions = () => [
  {
    id: "Admin",
    name: "Admin",
    description: "Full operational and administrative access across ClearHealth EMR.",
    permissionsV2: grantAllPermissions(),
  },
  {
    id: "Doctor",
    name: "Doctor",
    description: "Clinical workspace access for consultations, orders, diagnosis coding, and results review.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      appointments: clinicalRead,
      emergency: clinicalRead,
      consultations: ["view", "create", "edit", "export"],
      "diagnosis-master": clinicalRead,
      "nurses-station": clinicalRead,
      admission: ["view", "create", "edit"],
      wards: ["view", "create", "edit"],
      lab: ["view", "create", "export"],
      radiology: ["view", "create", "export"],
      pharmacy: clinicalRead,
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Nurse",
    name: "Nurse",
    description: "Nursing workflow access for vitals, notes, ward care, and patient handover.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      appointments: clinicalRead,
      emergency: operationalEdit,
      "nurses-station": ["view", "create", "edit", "export"],
      admission: ["view", "edit"],
      wards: ["view", "create", "edit", "export"],
      lab: clinicalRead,
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Receptionist",
    name: "Receptionist",
    description: "Front desk access for patient registration, appointments, family folders, and basic billing handoff.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: ["view", "create", "edit", "export"],
      family: ["view", "create", "edit", "export"],
      appointments: ["view", "create", "edit", "delete", "export"],
      emergency: ["view", "create", "edit"],
      billing: ["view", "create"],
      admission: ["view", "create"],
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Laboratory Staff",
    name: "Laboratory Staff",
    description: "Lab worklist, sample collection, MCS entry, review, and lab reporting access.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      lab: approveFlow,
      "mcs-settings": ["view", "create", "edit", "delete", "export"],
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Radiology Staff",
    name: "Radiology Staff",
    description: "Radiology worklist, templates, reporting, review, and export access.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      radiology: approveFlow,
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Pharmacist",
    name: "Pharmacist",
    description: "Pharmacy dispensing, prescription queue, inventory, and pharmacy reports.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      pharmacy: approveFlow,
      inventory: ["view", "create", "edit", "adjust", "delete", "export"],
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Billing Staff",
    name: "Billing Staff",
    description: "Billing, payments, invoice history, expense entry, and cashier reports.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      billing: ["view", "create", "edit", "delete", "export"],
      expenses: ["view", "create", "edit", "export"],
      schemes: clinicalRead,
      "scheme-desk": clinicalRead,
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Scheme Manager",
    name: "Scheme Manager",
    description: "Scheme setup, covered service registry, scheme patient management, and claims reports.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      billing: clinicalRead,
      schemes: ["view", "create", "edit", "delete", "approve", "export"],
      "scheme-desk": approveFlow,
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Scheme Desk Officer",
    name: "Scheme Desk Officer",
    description: "Scheme desk clearance, claim workflow, and scheme patient verification.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      billing: clinicalRead,
      schemes: clinicalRead,
      "scheme-desk": approveFlow,
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Ward Nurse",
    name: "Ward Nurse",
    description: "Ward patient care, charts, notes, and ward activity documentation.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: clinicalRead,
      "nurses-station": ["view", "create", "edit", "export"],
      admission: ["view", "edit"],
      wards: ["view", "create", "edit", "approve", "export"],
      lab: clinicalRead,
      pharmacy: clinicalRead,
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "HR Attendance Officer",
    name: "HR Attendance Officer",
    description: "Staff records, shift templates, duty plans, biometric devices, and attendance reports.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      staff: ["view", "create", "edit", "delete", "export"],
      "staff-duty": ["view", "create", "edit", "delete", "approve", "export"],
      biometrics: ["view", "create", "edit", "delete", "approve", "export"],
      reports: reporting,
      "learning-center": clinicalRead,
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "IT Department",
    name: "IT Department",
    description: "Technical support, configuration, staff/user setup, devices, learning content, and read-only troubleshooting across core modules.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      patients: ["view", "export"],
      family: clinicalRead,
      appointments: clinicalRead,
      emergency: clinicalRead,
      consultations: clinicalRead,
      "diagnosis-master": ["view", "create", "edit", "export"],
      "nurses-station": clinicalRead,
      admission: clinicalRead,
      wards: clinicalRead,
      lab: clinicalRead,
      "mcs-settings": ["view", "create", "edit", "export"],
      radiology: clinicalRead,
      pharmacy: clinicalRead,
      inventory: ["view", "export"],
      billing: clinicalRead,
      schemes: clinicalRead,
      "scheme-desk": clinicalRead,
      reports: reporting,
      staff: ["view", "create", "edit", "export"],
      "staff-duty": ["view", "create", "edit", "export"],
      biometrics: ["view", "create", "edit", "export"],
      settings: ["view", "edit", "export"],
      "roles-permissions": ["view", "create", "edit", "export"],
      "server-management": ["view", "edit"],
      "sync-status": ["view", "edit"],
      departments: ["view", "create", "edit", "export"],
      clinics: ["view", "create", "edit", "export"],
      "learning-center": ["view", "create", "edit"],
      "demo-mode": ["view", "create", "delete"],
    }),
  },
  {
    id: "Auditor",
    name: "Auditor",
    description: "Read-only access to local audit logs, financial corrections, ledger, and reports.",
    permissionsV2: buildRoleMap({
      dashboard: clinicalRead,
      reports: reporting,
      billing: clinicalRead,
      "admin-audit": reporting,
      "sync-status": clinicalRead,
      "demo-mode": ["view"],
    }),
  },
];

export const savePredefinedRolesToFirestore = async () => {
  if (usesLocalApi()) {
    const hospitalId = sessionStorage.getItem("hospitalId") || localStorage.getItem("hospitalId");
    if (!hospitalId || hospitalId === "null" || hospitalId === "undefined") {
      return { skipped: true, reason: "missing-hospital-id" };
    }
    return localPermissionService.ensureDefaultRoles(hospitalId);
  }

  try {
    const rolesRef = collection(db, "roles");
    const predefinedRoles = predefinedRoleDefinitions();
    const existingSnap = await getDocs(query(rolesRef, where("isPredefined", "==", true)));
    const existingById = new Map(existingSnap.docs.map((roleDoc) => [roleDoc.id, roleDoc.data()]));

    let synced = 0;
    for (const role of predefinedRoles) {
      const existing = existingById.get(role.id);
      if (existing?.registryVersion === PREDEFINED_ROLE_REGISTRY_VERSION) {
        continue;
      }

      const roleRef = doc(rolesRef, role.id);
      const permissionsV2 = normalizePermissionMap(role.permissionsV2);
      await setDoc(
        roleRef,
        {
          name: role.name,
          nameLower: role.name.toLowerCase(),
          description: role.description || "",
          permissionsV2,
          permissions: mapToArray(permissionsV2),
          isPredefined: true,
          hospitalId: null,
          registryVersion: PREDEFINED_ROLE_REGISTRY_VERSION,
          lastUpdated: new Date(),
        },
        { merge: true }
      );
      synced += 1;
    }

    return synced > 0;
  } catch (error) {
    console.error("Error saving predefined roles:", error.message);
    throw new Error("Failed to save predefined roles.");
  }
};

const coerceToPermsV2 = (role) => {
  if (role?.permissionsV2 && isPlainObject(role.permissionsV2)) {
    return normalizePermissionMap(role.permissionsV2);
  }
  if (Array.isArray(role?.permissions)) {
    return arrayToMap(role.permissions);
  }
  if (isPlainObject(role?.permissions)) {
    return normalizePermissionMap(role.permissions);
  }
  return emptyPermissionMap();
};

export const saveCustomRoleToFirestore = async (hospitalId, role) => {
  if (usesLocalApi()) {
    return localPermissionService.saveCustomRoleToFirestore(hospitalId, role);
  }

  try {
    if (!hospitalId) throw new Error("Hospital ID is required.");
    if (!role?.name?.trim()) throw new Error("Role name is required.");

    const nameTrim = role.name.trim();
    const nameLower = nameTrim.toLowerCase();
    const permissionsV2 = coerceToPermsV2(role);

    if (!hasAnyTrue(permissionsV2)) {
      throw new Error("Please grant at least one permission.");
    }

    const rolesRef = collection(db, "roles");
    const dupQ = query(
      rolesRef,
      where("hospitalId", "==", hospitalId),
      where("nameLower", "==", nameLower)
    );
    const dupSnap = await getDocs(dupQ);
    const conflict = dupSnap.docs.find((d) => d.id !== role.id);
    if (conflict) {
      throw new Error(`A role named "${nameTrim}" already exists for this hospital.`);
    }

    const roleRef = role.id ? doc(rolesRef, role.id) : doc(rolesRef);
    const roleData = {
      name: nameTrim,
      nameLower,
      description: role.description || "",
      permissionsV2,
      permissions: mapToArray(permissionsV2),
      isPredefined: false,
      hospitalId,
      lastUpdated: new Date(),
    };

    await setDoc(roleRef, roleData, { merge: true });

    return {
      success: true,
      message: "Role saved successfully.",
      id: roleRef.id,
    };
  } catch (error) {
    console.error("Error saving role:", error.message);
    throw new Error(error.message || "Failed to save role.");
  }
};

export const fetchCustomRoles = async (hospitalId) => {
  if (usesLocalApi()) {
    return localPermissionService.fetchCustomRoles(hospitalId);
  }

  try {
    if (!hospitalId) throw new Error("Hospital ID is required.");

    const rolesCollection = collection(db, "roles");
    const q = query(rolesCollection, where("hospitalId", "==", hospitalId));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((d) => {
      const data = d.data() || {};
      const permissionsV2 = coerceToPermsV2(data);
      return {
        id: d.id,
        name: data.name,
        description: data.description || "",
        isPredefined: !!data.isPredefined,
        hospitalId: data.hospitalId,
        permissionsV2,
        permissions: mapToArray(permissionsV2),
        lastUpdated: data.lastUpdated || null,
      };
    });
  } catch (error) {
    console.error("Error fetching custom roles:", error);
    return [];
  }
};

export const fetchPredefinedRoles = async () => {
  if (usesLocalApi()) {
    const hospitalId = sessionStorage.getItem("hospitalId") || localStorage.getItem("hospitalId");
    return localPermissionService.fetchPredefinedRoles(hospitalId);
  }

  try {
    const rolesCollection = collection(db, "roles");
    const q = query(rolesCollection, where("isPredefined", "==", true));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((d) => {
      const data = d.data() || {};
      const permissionsV2 = coerceToPermsV2(data);
      return {
        id: d.id,
        ...data,
        permissionsV2,
        permissions: mapToArray(permissionsV2),
      };
    });
  } catch (error) {
    console.error("Error fetching predefined roles:", error);
    return [];
  }
};

const deleteCustomRole = async (roleId) => {
  if (usesLocalApi()) {
    return localPermissionService.deleteCustomRole(roleId);
  }

  try {
    if (!roleId) throw new Error("Role ID is required.");
    const roleRef = doc(db, "roles", roleId);
    await deleteDoc(roleRef);
    return { success: true, message: "Role deleted successfully." };
  } catch (error) {
    console.error("Error deleting role:", error.message);
    throw new Error("Failed to delete role: " + error.message);
  }
};

export { deleteCustomRole };

export const fetchRolePermissions = async (hospitalId, roleName) => {
  if (usesLocalApi()) {
    return localPermissionService.fetchRolePermissions(hospitalId, roleName);
  }

  try {
    const rolesRef = collection(db, "roles");

    const customQ = query(
      rolesRef,
      where("name", "==", roleName),
      where("hospitalId", "==", hospitalId)
    );
    const customSnap = await getDocs(customQ);
    if (!customSnap.empty) {
      const permissionsV2 = coerceToPermsV2(customSnap.docs[0].data());
      return mapToArray(permissionsV2);
    }

    const predefinedQ = query(
      rolesRef,
      where("name", "==", roleName),
      where("isPredefined", "==", true)
    );
    const predefinedSnap = await getDocs(predefinedQ);
    if (!predefinedSnap.empty) {
      const permissionsV2 = coerceToPermsV2(predefinedSnap.docs[0].data());
      return mapToArray(permissionsV2);
    }

    console.warn(`Role "${roleName}" not found for hospital or global.`);
    return [];
  } catch (error) {
    console.error("Error fetching role permissions:", error.message);
    return [];
  }
};
